/**
 * Duce utilizatorul la primul camp marcat cu eroare.
 *
 * De ce exista: pe formularul de inscriere a scolii (~30 de campuri), backendul
 * intoarce `errors.<camp>` si `applyServerFieldErrors` chiar il pune pe camp — dar
 * campul vinovat poate fi la zeci de randuri deasupra butonului de trimitere, deci
 * utilizatorul vedea doar toastul si nu afla ce e gresit. Defectul T7/D6.
 *
 * Cautam `[aria-invalid="true"]`, nu numele campului: `FormControl`
 * (`components/ui/form.tsx`) pune atributul pe controlul randat, oricare ar fi el
 * (input, buton de combobox, textarea), iar `querySelector` intoarce primul din
 * ordinea documentului — adica primul camp invalid de sus in jos. Asa merge si
 * pentru controale custom care nu forwardeaza `ref` catre react-hook-form
 * (`SiiirSchoolSelector`), unde `setError(..., { shouldFocus: true })` n-ar face nimic.
 */

/** Cat de jos, din inaltimea vizibila, asezam campul: un sfert de sus. */
const FRACTIUNE_DE_SUS = 4;

/** Cat asteptam inainte de a reincerca focusul, dupa inchiderea unui dialog modal. */
const REINCERCARE_FOCUS_MS = 250;

function gasesteContainerulDerulabil(element: HTMLElement): HTMLElement | null {
    let parinte = element.parentElement;

    while (parinte) {
        const { overflowY } = window.getComputedStyle(parinte);
        const seDeruleaza = overflowY === 'auto' || overflowY === 'scroll';

        if (seDeruleaza && parinte.scrollHeight > parinte.clientHeight) return parinte;
        parinte = parinte.parentElement;
    }

    return null;
}

/**
 * Deruleaza la primul camp invalid, il focuseaza, si spune cate campuri sunt marcate.
 *
 * Ordinea conteaza pe mobil: intai pozitionam campul in treimea de sus, apoi
 * focusam cu `preventScroll`, ca tastatura virtuala (care acopera jumatatea de jos
 * a ecranului la 360px) sa nu ajunga peste el.
 *
 * Derularea e instanta, deliberat: peste un formular lung, `smooth` inseamna o
 * secunda de continut care zboara pe langa ochi, si contrazice `prefers-reduced-motion`.
 *
 * Numaram din DOM, nu din `formState.errors`: citit imediat dupa `await form.trigger()`,
 * obiectul de erori e instantaneul render-ului ANTERIOR, deci inca gol (verificat in
 * browser — toastul cadea pe mesajul de rezerva). Numarul din DOM e si cel pe care il
 * vede utilizatorul: cate campuri sunt efectiv marcate.
 *
 * @returns numarul de campuri invalide (0 daca niciunul)
 */
export function focusFirstInvalidField(): number {
    if (typeof document === 'undefined') return 0;

    const campuri = document.querySelectorAll<HTMLElement>('[aria-invalid="true"]');
    const camp = campuri[0];
    if (!camp) return 0;

    const container = gasesteContainerulDerulabil(camp);

    if (container) {
        const decalaj =
            camp.getBoundingClientRect().top -
            container.getBoundingClientRect().top -
            container.clientHeight / FRACTIUNE_DE_SUS;

        container.scrollBy({ top: decalaj, behavior: 'auto' });
    } else {
        const decalaj =
            camp.getBoundingClientRect().top - window.innerHeight / FRACTIUNE_DE_SUS;

        window.scrollBy({ top: decalaj, behavior: 'auto' });
    }

    camp.focus({ preventScroll: true });

    // Un dialog modal care se inchide imediat dupa submit isi ia focusul inapoi la
    // inchidere (`onCloseAutoFocus` din Radix). Cand butonul lui de confirmare e intre
    // timp dezactivat, focusul nu ajunge nicaieri si ramane pe `body` — masurat in
    // browser pe cererea de inscriere a scolii. Reincercam o singura data, si NUMAI
    // daca focusul chiar s-a pierdut: daca utilizatorul a mutat el focusul intre timp,
    // nu i-l luam inapoi.
    setTimeout(() => {
        const pierdut = document.activeElement === null || document.activeElement === document.body;
        if (pierdut && document.contains(camp)) camp.focus({ preventScroll: true });
    }, REINCERCARE_FOCUS_MS);

    return campuri.length;
}


/**
 * Textele mesajelor de eroare afisate acum in pagina.
 *
 * Serveste la a decide daca o eroare venita de la backend a ajuns pe un camp VIZIBIL sau pe unul
 * care nu se randeaza in formularul curent (vezi `selectInvisibleMessages`). `FormMessage`
 * (`components/ui/form.tsx`) marcheaza fiecare mesaj cu `data-slot="form-message"`.
 */
export function collectVisibleFieldMessages(): string[] {
    if (typeof document === 'undefined') return [];

    return [...document.querySelectorAll('[data-slot="form-message"]')]
        .map((nod) => (nod.textContent ?? '').trim())
        .filter(Boolean);
}
